/**
 * SchemaClass represents a class in the domain model (TBox).
 * Implements Node interface for unified handling with Individual (ABox).
 *
 * Renamed from ModelClass as part of the TBox/ABox refactoring.
 */

import Concept from "@semantical/modules/vocabulary/Concept.ts";
import Vocabulary from "@semantical/modules/vocabulary/Vocabulary.ts";
import type { Node } from "@semantical/modules/semantical-core";
import type { DataProperty } from "./DataProperty.ts";
import {
    classExpressionToJSON,
    classExpressionFromJSON,
    type OwlClassExpressionJSON,
    type OwlClassExpression
} from "./ClassExpression.ts";

/**
 * SchemaClass represents a class in the domain model (TBox).
 * Implements Node interface for unified handling with Individual.
 */
export default class SchemaClass implements Node {
    type: string;
    properties: string[] = [];
    proposedProperties: string[] = [];
    proposedDefinitions: any = [];
    concept: Concept; // non-null

    // Flattened fields (previously in owlClass)
    /** The class label (may differ from concept.prefLabel) */
    label?: string;
    /** Semantic stereotype (e.g., "Kind", "Role", "Phase") */
    stereotype?: string;
    /** Visual category color from ontology (sem:categoryColor) */
    categoryColor?: string;
    /** Data properties (attributes) defined for this class */
    dataProperties?: DataProperty[];

    // Private axiom fields (internal use only)
    /** @internal rdfs:subClassOf axioms (includes property restrictions) */
    _subClassOf?: OwlClassExpression[];
    /** @internal owl:equivalentClass axioms */
    _equivalentClass?: OwlClassExpression[];

    constructor(
        type: string,
        concept: Concept,
        options?: {
            label?: string;
            stereotype?: string;
            categoryColor?: string;
            dataProperties?: DataProperty[];
            subClassOf?: OwlClassExpression[];
            equivalentClass?: OwlClassExpression[];
        }
    ) {
        this.type = type;
        this.concept = concept;
        if (options) {
            this.label = options.label;
            this.stereotype = options.stereotype;
            this.categoryColor = options.categoryColor;
            this.dataProperties = options.dataProperties;
            this._subClassOf = options.subClassOf;
            this._equivalentClass = options.equivalentClass;
        }
    }

    get name(): string {
        return this.concept.prefLabel;
    }

    get definition(): string {
        return this.concept.definition;
    }

    /**
     * The identifying URI for this class.
     * Uses label-derived URI pattern, otherwise falls back to concept.uri.
     */
    get uri(): string {
        // If we have a label, we can derive a URI pattern
        // For now, concept.uri is the canonical identifier
        return this.concept.uri;
    }

    static fromJSON(json: any, vocabulary: Vocabulary) {
        if (!json.concept || !json.concept.href) {
            throw new Error("SchemaClass.fromJSON: concept reference is required");
        }

        // Try to get concept from vocabulary, or create from stored data if not found
        let concept: Concept;
        if (vocabulary.hasConcept(json.concept.href)) {
            concept = vocabulary.getConcept(json.concept.href);
        } else {
            // Concept not in vocabulary (e.g., created from toolbox with urn:concept:... URI)
            // Restore from stored data
            concept = new Concept(
                json.concept.href,
                json.concept.prefLabel || json.concept.href,
                [],
                [],
                json.concept.definition || ""
            );
        }

        // Restore flattened class data
        const schemaClass = new SchemaClass(json.type, concept, {
            label: json.label,
            stereotype: json.stereotype,
            categoryColor: json.categoryColor,
            dataProperties: json.dataProperties,
            subClassOf: json.subClassOf?.map((expr: OwlClassExpressionJSON) => classExpressionFromJSON(expr)),
            equivalentClass: json.equivalentClass?.map((expr: OwlClassExpressionJSON) => classExpressionFromJSON(expr))
        });
        schemaClass.properties = json.properties ?? [];
        schemaClass.proposedProperties = json.proposedProperties ?? [];
        schemaClass.proposedDefinitions = json.proposedDefinitions ?? [];
        return schemaClass;
    }

    toJSON() {
        return {
            type: this.type,
            properties: this.properties.map((property) => {
                // convert "+ fuelType: String" to {name: "fuelType", type: "String", cardinality: "1"} using a regexp
                const matches = property.match(/(\+|-)?\s*(\w+)\s*:\s*(\w+)/);
                return {
                    name: matches![2],
                    type: matches![3],
                    cardinality: "1"
                }
            }),
            concept: {
                href: this.concept.uri,
                // Also store full concept data for restoration when concept is not in vocabulary
                prefLabel: this.concept.prefLabel,
                definition: this.concept.definition
            },
            // Flattened fields (no longer nested in owlClass)
            label: this.label,
            stereotype: this.stereotype,
            categoryColor: this.categoryColor,
            dataProperties: this.dataProperties,
            subClassOf: this._subClassOf?.map(expr => classExpressionToJSON(expr)),
            equivalentClass: this._equivalentClass?.map(expr => classExpressionToJSON(expr))
        }
    }
}

// Re-export types
export type { DataProperty } from "./DataProperty.ts";
// RestrictedObjectProperty is the replacement for the deprecated OwlObjectPropertyRestriction
export type { RestrictedObjectProperty } from "./ClassExpression.ts";

/**
 * @deprecated Use SchemaClass instead
 */
export { SchemaClass as ModelClass };
